import { InformationPage } from "../../common/components/informationPage/InformationPage";
import { useIsMinDesktopSize } from "../../common/hooks/useMediaQuery";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { HOMEPAGE, STORE_FINDER } from "~/utils/url/staticUrls";
import { Grid, Link as MuiLink, Paper, Stack, styled, TextField, Typography } from "@mui/material";
import { C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";
import { LinkButton } from "~/common/components/Link";
import { useLoginApi } from "~/Login/components/LoginPopup";
import { useState } from "react";
import ResetPasswordPopup from "~/Login/components/ResetPasswordPopup";
import { IMG_REQUEST_SUCCESS } from "~/utils/url/staticResources";
import { registerUser } from "~/RegisterForFreePage/api/registerUserForFree";
import { resendUserEmail } from "~/RegisterForFreePage/api/resendUserEmail";

export interface UserData {
  name: string;
  company: string;
  email: string;
  address: string;
  registered: boolean;
  marketingConsent: boolean;
}

interface InquirySuccessPageProps {
  userData?: UserData;
}

export const InquirySuccessPage = ({ userData }: InquirySuccessPageProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const [isEmailSent, setIsEmailSent] = useState(false);

  let inquirySuccessComponent = (
    <InformationPage.CenterContents leftAlign={!!userData}>
      <InformationPage.Title>{t("feature.b2c.inquirySuccess.title")}</InformationPage.Title>
      <InformationPage.Image src={IMG_REQUEST_SUCCESS} />
      <InformationPage.Subtitle className={"f-pre-line"}>
        {t("feature.b2c.inquirySuccess.subtitle")}
      </InformationPage.Subtitle>
      {!userData && (
        <>
          <div className={"w-100"}>
            <Button fullWidth={!isDesktop} href={STORE_FINDER}>
              {t("feature.b2c.inquirySuccess.btn.sendMore")}
            </Button>
          </div>
          <MuiLink href={HOMEPAGE}>{t("feature.b2c.inquirySuccess.btn.continue")}</MuiLink>
        </>
      )}
    </InformationPage.CenterContents>
  );

  if (!userData) {
    return inquirySuccessComponent;
  }

  let userComponent;
  if (userData.registered) {
    userComponent = <RegisteredUserSection userData={userData} />;
  } else {
    userComponent = <UnregisteredUserSection userData={userData} onCompleteRegistration={() => setIsEmailSent(true)} />;
  }

  if (isEmailSent) {
    inquirySuccessComponent = <EmailSentSection />;
  }

  return (
    <Grid container spacing={3}>
      <Grid size={{ phone: 12, tablet: 6 }}>{inquirySuccessComponent}</Grid>
      <Grid size={{ phone: 12, tablet: 6 }}>{userComponent}</Grid>
    </Grid>
  );
};

const UserInfoItem = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <Grid size={6}>
      <Typography color="text.label">{label}</Typography>
      <TypographyWithWordBreak>{children}</TypographyWithWordBreak>
    </Grid>
  );
};

const RegisteredUserSection = ({ userData }: { userData: UserData }) => {
  const { t } = useTranslation();
  const { login, inFlight, errorComponent } = useLoginApi();
  const [password, setPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  return (
    <Paper sx={{ background: C_SURFACE_BRIGHT, p: 3 }}>
      <Grid container spacing={3}>
        <Grid>
          <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
            {t("feature.guestCheckout.completeRegistration.alreadyRegistered")}
          </Typography>
        </Grid>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.name")}>{userData.name}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.email")}>{userData.email}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.company")}>{userData.company}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.address")}>{userData.address}</UserInfoItem>

        <Grid size={12}>
          <Stack gap={2}>
            <TextField
              type="password"
              variant="outlined"
              label={t("feature.guestCheckout.completeRegistration.password")}
              fullWidth
              onChange={(e) => setPassword(e.target.value)}
            />
            <LinkButton onClick={() => setShowResetPassword(true)}>
              {t("feature.guestCheckout.completeRegistration.button.forgotPassword")}
            </LinkButton>
          </Stack>
        </Grid>
        {errorComponent && <Grid size={12}>{errorComponent}</Grid>}
        <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => login(userData.email, password)} loading={inFlight} disabled={inFlight || !password}>
            {t("page.login.loginBtn")}
          </Button>
        </Grid>
      </Grid>
      {showResetPassword && (
        <ResetPasswordPopup close={() => setShowResetPassword(false)} initialEmail={userData.email} disableEdit />
      )}
    </Paper>
  );
};

const UnregisteredUserSection = ({
  userData,
  onCompleteRegistration,
}: {
  userData: UserData;
  onCompleteRegistration: () => void;
}) => {
  const { t } = useTranslation();
  const [inFlight, setInFlight] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);

  const handleRegister = async () => {
    setInFlight(true);

    if (!sentOnce) {
      await registerUser({
        email: userData.email,
        marketingConsent: userData.marketingConsent,
        generalTerms: true,
        privacyPolicy: true,
        captcha: "",
      });
    } else {
      await resendUserEmail(userData.email);
    }

    onCompleteRegistration();
    setSentOnce(true);
    setInFlight(false);
  };

  const buttonLabel = t(
    `feature.guestCheckout.completeRegistration.button.${sentOnce ? "resendEmail" : "finishRegistration"}`,
  );

  return (
    <Paper sx={{ background: C_SURFACE_BRIGHT, p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
            {t("feature.guestCheckout.completeRegistration.finishRegistration")}
          </Typography>
        </Grid>

        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.name")}>{userData.name}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.email")}>{userData.email}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.company")}>{userData.company}</UserInfoItem>
        <UserInfoItem label={t("feature.guestCheckout.completeRegistration.address")}>{userData.address}</UserInfoItem>
        <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleRegister} loading={inFlight}>
            {buttonLabel}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const EmailSentSection = () => {
  const { t } = useTranslation();
  return (
    <InformationPage.CenterContents leftAlign={true}>
      <InformationPage.Title>{t("feature.guestCheckout.completeRegistration.emailHasBeenSent")}</InformationPage.Title>
      <InformationPage.Image src={IMG_REQUEST_SUCCESS} />
      <InformationPage.Subtitle className={"f-pre-line"}>
        {t("feature.b2c.inquirySuccess.subtitle")}
      </InformationPage.Subtitle>
    </InformationPage.CenterContents>
  );
};

const TypographyWithWordBreak = styled(Typography)({
  wordBreak: "break-all",
});
